import * as React from 'react';

import { useAnchorReflow } from '@/components/ui/use-anchor-reflow';

// =============================================================================
// use-anchored-popover.ts — Socle commun des panneaux PORTALÉS ancrés sur leur déclencheur.
// =============================================================================
// EXTRAIT LE 2026-09-21, À L'IDENTIQUE, de `FilterPopover` (routes/admin/operational-view/
// operational-view-filter-menu.tsx) pour que `FilterCombo` (components/ui/filter-combo.tsx) en HÉRITE
// au lieu de le réécrire. Le mécanisme déplacé ici est exactement celui déjà éprouvé en production :
//   1. ouverture / fermeture avec restitution du focus au déclencheur ;
//   2. ancrage en `position: fixed` sous le déclencheur, RECALCULÉ au défilement, au redimensionnement
//      et au zoom (`useAnchorReflow`, U1-bis) — le panneau est ré-ancré, jamais fermé ;
//   3. BASCULE au-dessus du déclencheur quand la place manque sous lui (zoom 150 %) ;
//   4. fermeture au clic hors du déclencheur ET hors du panneau (qui vit dans <body>) ;
//   5. crochet `onOpened` appelé UNE fois par ouverture, une fois le panneau monté et ancré, pour que
//      l'appelant pose le focus où SON contenu l'exige (1re case pour FilterPopover, champ de filtre
//      pour FilterCombo).
//
// Ce qui reste à l'appelant : le rendu du déclencheur, le rendu du panneau et SON clavier. C'est
// précisément ce qui diffère entre un menu de cases et une liste autofiltrante ; tout le reste est
// commun et n'a plus qu'une seule implémentation.

type Anchor =
  | { readonly top: number; readonly left: number }
  | { readonly top: number; readonly right: number };

/** Écart vertical (px) entre le déclencheur et le panneau (parité menu-button). */
const ANCHOR_GAP = 4;
/** Marge minimale (px) entre le panneau et le bord du viewport (il ne sort jamais de l'écran). */
const VIEWPORT_MARGIN = 8;

export interface AnchoredPopoverOptions {
  /** Alignement du panneau sous le déclencheur. */
  readonly align?: 'start' | 'end';
  /** Appelé une seule fois par ouverture, panneau monté et ancré : l'appelant y pose le focus. */
  readonly onOpened?: (popup: HTMLDivElement) => void;
}

export interface AnchoredPopover {
  readonly open: boolean;
  /** Identifiant du panneau, à poser sur lui et sur l'`aria-controls` du déclencheur. */
  readonly popupId: string;
  readonly triggerRef: React.RefObject<HTMLButtonElement | null>;
  readonly popupRef: React.RefObject<HTMLDivElement | null>;
  /** Style du panneau (position fixe + ancrage). `null` = pas encore ancré : ne rien rendre. */
  readonly popupStyle: React.CSSProperties | null;
  readonly openPopover: () => void;
  readonly toggle: () => void;
  /** Ferme ; `restoreFocus` rend le focus au déclencheur (Échap, Tab sortant), sinon non (clic ailleurs). */
  readonly close: (restoreFocus: boolean) => void;
}

export function useAnchoredPopover({
  align = 'start',
  onOpened,
}: AnchoredPopoverOptions = {}): AnchoredPopover {
  const [open, setOpen] = React.useState(false);
  const [anchor, setAnchor] = React.useState<Anchor | null>(null);
  const triggerRef = React.useRef<HTMLButtonElement | null>(null);
  const popupRef = React.useRef<HTMLDivElement | null>(null);
  // Le focus n'est posé qu'à l'OUVERTURE (pas à chaque ré-ancrage au scroll).
  const focusOnOpenRef = React.useRef(false);
  // `onOpened` est une closure recréée à chaque rendu de l'appelant. La garder dans une ref évite de
  // relancer l'effet de focus à chaque frappe clavier, ce qui VOLERAIT le focus à l'utilisateur.
  const onOpenedRef = React.useRef(onOpened);
  const popupId = React.useId();

  React.useEffect(() => {
    onOpenedRef.current = onOpened;
  });

  const updateAnchor = React.useCallback((): void => {
    if (triggerRef.current === null) {
      return;
    }
    const r = triggerRef.current.getBoundingClientRect();
    // Le panneau BASCULE au-dessus du déclencheur quand il n'y a plus la place en dessous (cas du zoom
    // 150 %, où le viewport se réduit) : il reste COLLÉ à son bouton et visible, jamais rogné.
    const popupHeight = popupRef.current?.getBoundingClientRect().height ?? 0;
    const below = r.bottom + ANCHOR_GAP;
    const flip =
      popupHeight > 0 &&
      below + popupHeight > window.innerHeight - VIEWPORT_MARGIN &&
      r.top - ANCHOR_GAP - popupHeight >= VIEWPORT_MARGIN;
    const top = flip ? r.top - ANCHOR_GAP - popupHeight : below;
    setAnchor(
      align === 'end'
        ? { top, right: Math.max(VIEWPORT_MARGIN, window.innerWidth - r.right) }
        : { top, left: Math.max(VIEWPORT_MARGIN, r.left) },
    );
  }, [align]);

  const close = React.useCallback((restoreFocus: boolean): void => {
    setOpen(false);
    if (restoreFocus) {
      triggerRef.current?.focus();
    }
  }, []);

  const openPopover = React.useCallback((): void => {
    setOpen(true);
  }, []);

  const toggle = React.useCallback((): void => {
    setOpen((v) => !v);
  }, []);

  React.useLayoutEffect(() => {
    if (open) {
      focusOnOpenRef.current = true;
      updateAnchor();
    }
  }, [open, updateAnchor]);

  React.useEffect(() => {
    if (open && anchor !== null && focusOnOpenRef.current && popupRef.current !== null) {
      focusOnOpenRef.current = false;
      onOpenedRef.current?.(popupRef.current);
    }
  }, [open, anchor]);

  // Clic hors du déclencheur ET du panneau (portalé) : ferme sans voler le focus.
  React.useEffect(() => {
    if (!open) {
      return;
    }
    const onPointerDown = (event: MouseEvent): void => {
      const target = event.target as Node;
      const inTrigger = triggerRef.current?.contains(target) === true;
      const inPopup = popupRef.current?.contains(target) === true;
      if (!inTrigger && !inPopup) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
    };
  }, [open]);

  // Défilement (tout conteneur), redimensionnement ET ZOOM navigateur : le panneau est RÉ-ANCRÉ sur son
  // déclencheur, jamais fermé (U1-bis — « la list of values … apparaît en mode floating quand on zoom »).
  useAnchorReflow(open, updateAnchor);

  const popupStyle = React.useMemo<React.CSSProperties | null>(
    () => (anchor === null ? null : { position: 'fixed', ...anchor }),
    [anchor],
  );

  return { open, popupId, triggerRef, popupRef, popupStyle, openPopover, toggle, close };
}
